import React, { useCallback, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import toast from "react-hot-toast";
import InputDatePicker from "../../../../EvaluationNew/components/InputDatePicker";
import FormSignatureSections from "../FormSignatureSections";
import useFormSignatures from "../useFormSignatures";
import {
  FORM_FLAG_MESSAGES,
  CheckboxGrid,
  ExclusiveChoiceGrid,
  Field,
  FormActionBar,
  FormIdentityStrip,
  FormLoadingSkeleton,
  FormMetaRow,
  FormPageHeader,
  LockedReasonBanner,
  OfficeCommentsNote,
  OfficeReviewSection,
  SHEET,
  Section,
  fieldClass,
  textareaClass,
} from "../formLayout";
import {
  createPatientClinicalForm,
  getStVisitNoteForm,
  updateStVisitNoteForm,
} from "../../../../../services/patientService";
import {
  BrainIcon,
  CalendarIcon,
  CommentsIcon,
  DeleteIcon,
  FileCheckIcon,
  HeartPulseIcon,
  HomeIcon,
  MedicalNotesIcon,
  PlusIcon,
  RunningIcon,
  TargetIcon,
} from "../../../../../Icons";
import { displayDateUS, formatDateForInput } from "../../../../../utils/dateUtils";
import { PATIENT_PAGE_SHELL } from "../../../patientPageLayout";
import { saveDedicatedForm } from "../clinicalFormDraft";
import {
  draftCaregiverIdentity,
  useFormCaregiverPicker,
} from "../formCaregiver";
import { buildClinicalFormPermissions } from "../clinicalFormPermissions";
import { useClinicalCosignPermissions } from "../hooks/useClinicalCosignPermissions";
import {
  applyDisciplineFormPermissions,
  resolveFormActorRole,
  ST_FORM_EDITOR_ROLES,
} from "../disciplineFormAccess";
import {
  ARTICULATION_OPTIONS,
  ASPIRATION_SIGN_OPTIONS,
  COGNITIVE_ASSESSMENT_OPTIONS,
  DIET_CONSISTENCY_OPTIONS,
  EDUCATION_TOPIC_OPTIONS,
  EXPRESSIVE_STATUS_OPTIONS,
  FLUENCY_OPTIONS,
  GOAL_STATUS_OPTIONS,
  INTERVENTION_OPTIONS,
  LIQUID_CONSISTENCY_OPTIONS,
  RECEPTIVE_STATUS_OPTIONS,
  ST_VISIT_ARRAY_KEYS,
  VOICE_OPTIONS,
  emptyProgressGoalRow,
  emptyStVisitPayload,
  normalizeProgressGoals,
  parsePainLevel,
  toggleInList,
  today,
} from "./stVisitNoteContent";
import {
  assertPtVisitMetaForFlag,
  buildPtVisitMetaProps,
  pickPtVisitMeta,
} from "../ptNotes/ptVisitCommon";
import StNotesCommonHeader from "./StNotesCommonHeader";
import PainScalePills from "../ptNotes/PainScalePills";
import {
  normalizeStCommonHeaderState,
  resolveStNotesCommonHeader,
  withNormalizedStCommonHeader,
} from "./stNotesCommonHeaderUtils";

const VISIT_DATE_MAX = new Date().toISOString().slice(0, 10);

const emptyValues = () => ({
  ...emptyStVisitPayload(),
  visitDate: today(),
  clinicianSignatureName: "",
  patientSignatureName: "",
  reviewComments: "",
});

const DateFieldInput = ({ name, value, canEdit, printMode, onChange, max }) => {
  if (printMode) {
    return (
      <div className={`${fieldClass} flex items-center`}>
        {displayDateUS(value) || "—"}
      </div>
    );
  }
  return (
    <InputDatePicker
      name={name}
      formatSeparator="/"
      allowManualEntry
      disabled={!canEdit}
      value={formatDateForInput(value) || ""}
      onChange={(event) => onChange(event.target.value)}
      max={max}
      className="w-full"
      inputClassName={`${fieldClass} bg-white`}
    />
  );
};

DateFieldInput.propTypes = {
  name: PropTypes.string.isRequired,
  value: PropTypes.string,
  canEdit: PropTypes.bool,
  printMode: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
  max: PropTypes.string,
};

const goalStatusClass = (status) => {
  if (status === "Met") return "text-emerald-700 font-semibold";
  if (status === "In Progress") return "text-indigo-800 font-bold";
  if (status === "Partially Met") return "text-amber-700 font-medium";
  if (status === "Not Met") return "text-rose-700 font-medium";
  return "";
};

const StVisitNoteForm = ({
  patient,
  clinicalForm,
  draft = null,
  onBack,
  onCreated,
  printMode = false,
  onReady,
}) => {
  const patientId = clinicalForm.patientId;
  const formId = clinicalForm.id || null;
  const isDraft = !formId;

  const [form, setForm] = useState(null);
  const [values, setValues] = useState(emptyValues);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);

  const signatures = useFormSignatures();
  const { reset: resetSignatures } = signatures;

  const currentUser = useMemo(
    () => JSON.parse(localStorage.getItem("currentUser") || "{}"),
    [],
  );
  const role = resolveFormActorRole(currentUser);

  const patientName = useMemo(
    () => [patient?.lastName, patient?.firstName].filter(Boolean).join(", "),
    [patient],
  );

  const hydrate = useCallback(
    (data) => {
      setForm(data);
      const base = emptyStVisitPayload();
      const loadedPayload =
        data.payload &&
        typeof data.payload === "object" &&
        !Array.isArray(data.payload)
          ? data.payload
          : {};

      const merged = {
        ...base,
        ...loadedPayload,
        ...pickPtVisitMeta(loadedPayload, data),
        visitDate: data.visitDate || loadedPayload.visitDate || today(),
        commonHeader: normalizeStCommonHeaderState(
          loadedPayload.commonHeader || base.commonHeader,
        ),
        painLevel: parsePainLevel(loadedPayload.painLevel),
        progressGoals: normalizeProgressGoals(loadedPayload.progressGoals),
        patientSignatureName:
          data.patientSignatureName || data.patientName || patientName,
        clinicianSignatureName: data.clinicianSignatureName || "",
        reviewComments: data.reviewComments || "",
      };

      ST_VISIT_ARRAY_KEYS.forEach((key) => {
        merged[key] = Array.isArray(loadedPayload[key])
          ? loadedPayload[key].map(String)
          : [];
      });

      setValues(merged);
      resetSignatures();
    },
    [patientName, resetSignatures],
  );

  useEffect(() => {
    let cancelled = false;
    setLoading(true);

    const finish = () => {
      if (cancelled) return;
      setLoading(false);
      onReady?.();
    };

    const load = async () => {
      try {
        if (isDraft) {
          const commonHeader = await resolveStNotesCommonHeader({
            patient,
            patientId,
            payload: {},
          });
          if (cancelled) return;
          hydrate({
            formName:
              clinicalForm.formName ||
              draft?.formName ||
              "Speech Therapy Visit Note",
            formType: clinicalForm.formType || draft?.formType,
            formSection: clinicalForm.formSection || draft?.formSection,
            status: "pending",
            patientName,
            patientDateOfBirth: patient?.dateOfBirth || null,
            ...draftCaregiverIdentity(currentUser, role),
            createdAt: new Date().toISOString(),
            permissions: buildClinicalFormPermissions("pending", role),
            visitDate: today(),
            clinicianSignatureName: "",
            patientSignatureName: patientName,
            reviewComments: "",
            payload: {
              ...emptyStVisitPayload(),
              commonHeader,
            },
          });
          return;
        }

        const res = await getStVisitNoteForm(patientId, formId);
        if (cancelled) return;
        const loaded = res.result?.form || res.form;
        const existingPayload =
          loaded.payload && typeof loaded.payload === "object"
            ? loaded.payload
            : {};
        const commonHeader = await resolveStNotesCommonHeader({
          patient,
          patientId,
          payload: existingPayload,
        });
        if (cancelled) return;
        hydrate({
          ...loaded,
          payload: { ...existingPayload, commonHeader },
        });
      } catch (err) {
        if (cancelled) return;
        toast.error(err.message || "Failed to load form");
        onBack();
      } finally {
        finish();
      }
    };

    load();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientId, formId, isDraft]);

  const setValue = (key, value) =>
    setValues((prev) => ({ ...prev, [key]: value }));

  const toggleListValue = (key, item) => {
    setValues((prev) => {
      let next = toggleInList(prev[key], item);
      if (key === "aspirationSigns") {
        if (item === "None" && next.includes("None")) {
          next = ["None"];
        } else if (item !== "None") {
          next = next.filter((x) => x !== "None");
        }
      }
      return { ...prev, [key]: next };
    });
  };

  const updateGoalRow = (id, patch) => {
    setValues((prev) => ({
      ...prev,
      progressGoals: (prev.progressGoals || []).map((row) =>
        row.id === id ? { ...row, ...patch } : row,
      ),
    }));
  };

  const addGoalRow = () => {
    setValues((prev) => ({
      ...prev,
      progressGoals: [
        ...(prev.progressGoals || []),
        emptyProgressGoalRow((prev.progressGoals || []).length + 1),
      ],
    }));
  };

  const removeGoalRow = (id) => {
    setValues((prev) => {
      const next = (prev.progressGoals || []).filter((row) => row.id !== id);
      return {
        ...prev,
        progressGoals: next.length
          ? next
          : normalizeProgressGoals([], 1),
      };
    });
  };

  const { permissions: basePermissions } = useClinicalCosignPermissions({
    status: form?.status || "pending",
    role,
    actorUserId: currentUser?.id ? Number(currentUser.id) : null,
    formFilledByUserId:
      form?.filledBy ||
      clinicalForm?.filledBy ||
      (currentUser?.id ? Number(currentUser.id) : null),
    formFilledByRole: form?.filledByRole || clinicalForm?.filledByRole || role,
    serverPermissions: form?.permissions,
  });

  const permissions = useMemo(
    () =>
      applyDisciplineFormPermissions(
        basePermissions,
        role,
        ST_FORM_EDITOR_ROLES,
        "View only — only ST can edit this form.",
      ),
    [basePermissions, role],
  );

  const canEdit = !printMode && Boolean(permissions.canEdit);
  const isReviewer = Boolean(permissions.canReview);
  const canSign = !printMode && Boolean(permissions.canSignSignatures);

  const { caregiverSlot, filledByUserId, assertCaregiverForCreate } =
    useFormCaregiverPicker({
      patientId,
      formType: form?.formType || draft?.formType || clinicalForm?.formType,
      role,
      canEdit,
      preferredStaffId: draft?.visit?.staffId ?? null,
      currentFilledByUserId: form?.filledBy || clinicalForm?.filledBy || null,
    });

  const handleClinicianSign = (signature, signerName) => {
    signatures.handleClinicianSign(signature);
    setValues((prev) => ({
      ...prev,
      clinicianSignatureName: signerName || prev.clinicianSignatureName,
    }));
  };

  const buildPayload = (flag) => {
    const {
      clinicianSignatureName,
      patientSignatureName,
      reviewComments,
      visitDate,
      ...stFields
    } = values;

    const nestedPayload = withNormalizedStCommonHeader({
      ...stFields,
      painLevel:
        values.painLevel != null && values.painLevel !== ""
          ? Number(values.painLevel)
          : null,
      nextScheduledDate: values.nextScheduledDate
        ? formatDateForInput(values.nextScheduledDate)
        : "",
      progressGoals: (values.progressGoals || []).map((row) => ({
        id: row.id,
        goal: row.goal || "",
        status: row.status || "",
        accPercent: row.accPercent || "",
      })),
    });

    ST_VISIT_ARRAY_KEYS.forEach((key) => {
      nestedPayload[key] = Array.isArray(values[key]) ? values[key] : [];
    });

    const payload = {
      flag,
      visitDate: visitDate ? formatDateForInput(visitDate) : null,
      billingCode: values.billingCode || "",
      timeIn: values.timeIn || "",
      timeOut: values.timeOut || "",
      payload: nestedPayload,
      clinicianSignatureName: clinicianSignatureName || "",
      patientSignatureName: patientSignatureName || "",
      reviewComments: reviewComments || "",
    };

    if (!canSign) return payload;
    return { ...payload, ...signatures.buildSignaturePayload() };
  };

  const submit = async (flag) => {
    if (flag === "to_be_corrected" && !values.reviewComments?.trim()) {
      toast.error("Add review comments before marking the form to be corrected");
      return;
    }
    const caregiverError = assertCaregiverForCreate();
    if (caregiverError) {
      toast.error(caregiverError);
      return;
    }
    const visitMetaError = assertPtVisitMetaForFlag(values, flag);
    if (visitMetaError) {
      toast.error(visitMetaError);
      return;
    }
    setSaving(flag);
    try {
      const { form: saved, createdClinicalFormId } = await saveDedicatedForm({
        patientId,
        clinicalForm,
        draft,
        flag,
        payload: buildPayload(flag),
        createClinicalForm: createPatientClinicalForm,
        updateDedicatedForm: updateStVisitNoteForm,
        filledByUserId,
        scheduleDate: formatDateForInput(values.visitDate) || today(),
      });
      hydrate(saved);
      toast.success(FORM_FLAG_MESSAGES[flag] || "Form updated");
      if (createdClinicalFormId) onCreated?.(createdClinicalFormId);
    } catch (err) {
      toast.error(err.message || "Failed to save form");
    } finally {
      setSaving(null);
    }
  };

  if (loading) return <FormLoadingSkeleton />;
  if (!form) return null;

  const signatureProps = signatures.sectionProps(form);

  return (
    <div className={PATIENT_PAGE_SHELL}>
      {!printMode && (
        <FormPageHeader
          patientName={patientName}
          mrn={patient?.mrn}
          status={form.status}
          onBack={onBack}
        />
      )}

      <div
        className={`${SHEET} bg-white rounded-xl border border-slate-200 shadow-sm px-5 sm:px-8 py-6 space-y-3`}
      >
        <header className="text-center pb-1">
          <h1 className="text-[13px] font-bold uppercase tracking-[0.16em] text-slate-800">
            {form.formName || "Speech Therapy Visit Note"}
          </h1>
          <span className="mx-auto mt-2 block h-0.5 w-14 rounded-full bg-indigo-300" />
        </header>

        <FormIdentityStrip
          form={form}
          patientName={patientName || form.patientName}
          mrn={patient?.mrn}
          dateOfBirth={displayDateUS(
            patient?.dateOfBirth ||
              values.commonHeader?.dateOfBirth ||
              form.patientDateOfBirth,
          )}
          caregiverSlot={caregiverSlot}
          dateLabel="Visit Date"
          dateValue={displayDateUS(values.visitDate)}
          dateSlot={
            printMode ? null : (
              <div className="min-w-0">
                <div className="text-[7px] font-semibold uppercase tracking-wider text-slate-400 leading-none">
                  Visit Date
                </div>
                <InputDatePicker
                  name="visitDate"
                  formatSeparator="/"
                  allowManualEntry
                  compact
                  disabled={!canEdit}
                  value={formatDateForInput(values.visitDate) || ""}
                  onChange={(event) => setValue("visitDate", event.target.value)}
                  max={VISIT_DATE_MAX}
                  className="mt-0.5 min-w-0"
                  inputClassName="w-full h-4 text-[10px] font-bold bg-transparent border-0 p-0 outline-none"
                />
              </div>
            )
          }
          visitMeta={buildPtVisitMetaProps({
            values,
            setValue,
            setValues,
            canEdit,
            printMode,
          })}
        />

        <FormMetaRow form={form} printMode={printMode} />
        {!printMode && <LockedReasonBanner reason={permissions.lockedReason} />}

        <StNotesCommonHeader
          value={values.commonHeader}
          canEdit={canEdit}
          printMode={printMode}
          onChange={(next) => setValue("commonHeader", next)}
        />

        {/* 7. Subjective Findings */}
        <Section icon={MedicalNotesIcon} title="7. Subjective Findings">
          <Field label="Patient / Caregiver Report">
            <textarea
              rows={4}
              className={`${textareaClass} min-h-[5.5rem]`}
              value={values.patientCaregiverReport || ""}
              disabled={!canEdit}
              placeholder="Report of symptoms, progress, and concerns since last visit…"
              onChange={(e) =>
                setValue("patientCaregiverReport", e.target.value)
              }
            />
          </Field>
          <div className="mt-2 max-w-2xl">
            <PainScalePills
              label="Pain Level (0–10)"
              name="painLevel"
              value={values.painLevel}
              canEdit={canEdit}
              onChange={(v) => setValue("painLevel", v)}
            />
          </div>
        </Section>

        {/* 8. Speech Assessment */}
        <Section icon={HeartPulseIcon} title="8. Speech Assessment">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-x-3 gap-y-2">
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-500 mb-1">
                Articulation
              </p>
              <ExclusiveChoiceGrid
                name="articulation"
                options={ARTICULATION_OPTIONS}
                value={values.articulation || ""}
                canEdit={canEdit}
                onChange={(opt) => setValue("articulation", opt)}
                columns={3}
              />
            </div>
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-500 mb-1">
                Voice
              </p>
              <ExclusiveChoiceGrid
                name="voice"
                options={VOICE_OPTIONS}
                value={values.voice || ""}
                canEdit={canEdit}
                onChange={(opt) => setValue("voice", opt)}
                columns={3}
              />
            </div>
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-500 mb-1">
                Fluency
              </p>
              <ExclusiveChoiceGrid
                name="fluency"
                options={FLUENCY_OPTIONS}
                value={values.fluency || ""}
                canEdit={canEdit}
                onChange={(opt) => setValue("fluency", opt)}
                columns={2}
              />
            </div>
          </div>
        </Section>

        {/* 9. Language Assessment */}
        <Section icon={CommentsIcon} title="9. Language Assessment">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-3 gap-y-2">
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-500 mb-1">
                Expressive Status
              </p>
              <ExclusiveChoiceGrid
                name="expressiveStatus"
                options={EXPRESSIVE_STATUS_OPTIONS}
                value={values.expressiveStatus || ""}
                canEdit={canEdit}
                onChange={(opt) => setValue("expressiveStatus", opt)}
                columns={3}
              />
            </div>
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-500 mb-1">
                Receptive Status
              </p>
              <ExclusiveChoiceGrid
                name="receptiveStatus"
                options={RECEPTIVE_STATUS_OPTIONS}
                value={values.receptiveStatus || ""}
                canEdit={canEdit}
                onChange={(opt) => setValue("receptiveStatus", opt)}
                columns={3}
              />
            </div>
          </div>
        </Section>

        {/* 10. Cognitive Assessment */}
        <Section icon={BrainIcon} title="10. Cognitive Assessment">
          <CheckboxGrid
            options={COGNITIVE_ASSESSMENT_OPTIONS}
            values={values.cognitiveAssessment}
            canEdit={canEdit}
            onToggle={(opt) => toggleListValue("cognitiveAssessment", opt)}
            columns={2}
          />
        </Section>

        {/* 11. Swallowing/Dysphagia Assessment */}
        <Section icon={RunningIcon} title="11. Swallowing / Dysphagia Assessment">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-x-3 gap-y-2 items-end">
            <Field label="Diet Consistency">
              {printMode || !canEdit ? (
                <div className={`${fieldClass} flex items-center`}>
                  {values.dietConsistency || "—"}
                </div>
              ) : (
                <select
                  className={fieldClass}
                  value={values.dietConsistency || ""}
                  onChange={(e) => setValue("dietConsistency", e.target.value)}
                >
                  <option value="">Select…</option>
                  {DIET_CONSISTENCY_OPTIONS.map((opt) => (
                    <option key={opt} value={opt}>
                      {opt}
                    </option>
                  ))}
                </select>
              )}
            </Field>
            <Field label="Liquid Consistency">
              {printMode || !canEdit ? (
                <div className={`${fieldClass} flex items-center`}>
                  {values.liquidConsistency || "—"}
                </div>
              ) : (
                <select
                  className={fieldClass}
                  value={values.liquidConsistency || ""}
                  onChange={(e) =>
                    setValue("liquidConsistency", e.target.value)
                  }
                >
                  <option value="">Select…</option>
                  {LIQUID_CONSISTENCY_OPTIONS.map((opt) => (
                    <option key={opt} value={opt}>
                      {opt}
                    </option>
                  ))}
                </select>
              )}
            </Field>
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-500 mb-1">
                Aspiration Signs
              </p>
              <CheckboxGrid
                options={ASPIRATION_SIGN_OPTIONS}
                values={values.aspirationSigns}
                canEdit={canEdit}
                onToggle={(opt) => toggleListValue("aspirationSigns", opt)}
                columns={2}
              />
            </div>
          </div>
        </Section>

        {/* 12. Interventions Provided */}
        <Section icon={HeartPulseIcon} title="12. Interventions Provided">
          <CheckboxGrid
            options={INTERVENTION_OPTIONS}
            values={values.interventions}
            canEdit={canEdit}
            onToggle={(opt) => toggleListValue("interventions", opt)}
            columns={2}
          />
        </Section>

        {/* 13. Patient/Caregiver Education */}
        <Section icon={FileCheckIcon} title="13. Patient / Caregiver Education">
          <CheckboxGrid
            options={EDUCATION_TOPIC_OPTIONS}
            values={values.educationTopics}
            canEdit={canEdit}
            onToggle={(opt) => toggleListValue("educationTopics", opt)}
            columns={2}
          />
          <Field label="Education Details" span="mt-2">
            <textarea
              rows={3}
              className={`${textareaClass} min-h-[4.5rem]`}
              value={values.educationDetails || ""}
              disabled={!canEdit}
              placeholder="Instruction provided, caregiver demonstration, accuracy…"
              onChange={(e) => setValue("educationDetails", e.target.value)}
            />
          </Field>
        </Section>

        {/* 14. Progress Toward Goals */}
        <Section icon={TargetIcon} title="14. Progress Toward Goals">
          <div className="overflow-x-auto rounded-md border border-slate-200">
            <table className="w-full table-fixed text-left text-[11px]">
              <thead>
                <tr className="bg-slate-50 text-[9px] font-semibold uppercase tracking-wide text-slate-500">
                  <th className="px-2.5 py-2">Goal</th>
                  <th className="px-2.5 py-2 w-[22%]">Status</th>
                  <th className="px-2.5 py-2 w-[14%]">ACC%</th>
                  {canEdit ? (
                    <th className="px-2.5 py-2 w-10 text-right"> </th>
                  ) : null}
                </tr>
              </thead>
              <tbody>
                {(values.progressGoals || []).map((row) => (
                  <tr key={row.id} className="border-t border-slate-100">
                    <td className="px-2 py-1.5 align-middle">
                      <input
                        type="text"
                        className={fieldClass}
                        value={row.goal || ""}
                        disabled={!canEdit}
                        placeholder="Goal description…"
                        onChange={(e) =>
                          updateGoalRow(row.id, { goal: e.target.value })
                        }
                      />
                    </td>
                    <td className="px-2 py-1.5 align-middle">
                      {printMode || !canEdit ? (
                        <div
                          className={`${fieldClass} flex items-center ${goalStatusClass(row.status)}`}
                        >
                          {row.status || "—"}
                        </div>
                      ) : (
                        <select
                          className={`${fieldClass} ${goalStatusClass(row.status)}`}
                          value={row.status || ""}
                          onChange={(e) =>
                            updateGoalRow(row.id, { status: e.target.value })
                          }
                        >
                          <option value="">Select…</option>
                          {GOAL_STATUS_OPTIONS.map((opt) => (
                            <option key={opt} value={opt}>
                              {opt}
                            </option>
                          ))}
                        </select>
                      )}
                    </td>
                    <td className="px-2 py-1.5 align-middle">
                      <input
                        type="text"
                        className={fieldClass}
                        value={row.accPercent || ""}
                        disabled={!canEdit}
                        placeholder="e.g., 95%"
                        onChange={(e) =>
                          updateGoalRow(row.id, {
                            accPercent: e.target.value,
                          })
                        }
                      />
                    </td>
                    {canEdit ? (
                      <td className="px-2 py-1.5 align-middle text-right">
                        <button
                          type="button"
                          className="inline-flex h-7 w-7 items-center justify-center rounded-md border border-slate-200 text-slate-500 hover:bg-slate-50"
                          aria-label="Remove goal"
                          onClick={() => removeGoalRow(row.id)}
                        >
                          <DeleteIcon className="h-3.5 w-3.5" />
                        </button>
                      </td>
                    ) : null}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {canEdit ? (
            <button
              type="button"
              className="mt-2 inline-flex items-center gap-1 text-[11px] font-semibold text-indigo-600 hover:text-indigo-700"
              onClick={addGoalRow}
            >
              <PlusIcon className="h-3.5 w-3.5" />
              Add Goal
            </button>
          ) : null}
        </Section>

        {/* 15. Home Exercise/Program Recommendations */}
        <Section icon={HomeIcon} title="15. Home Exercise / Program Recommendations">
          <textarea
            rows={3}
            className={`${textareaClass} min-h-[4.5rem]`}
            value={values.homeExerciseRecommendations || ""}
            disabled={!canEdit}
            placeholder="Daily practice, caregiver monitoring, mealtime precautions…"
            onChange={(e) =>
              setValue("homeExerciseRecommendations", e.target.value)
            }
          />
        </Section>

        {/* 16. Plan for Next Visit */}
        <Section icon={CalendarIcon} title="16. Plan for Next Visit">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-3 gap-y-2">
            <Field label="Frequency">
              <input
                type="text"
                className={fieldClass}
                value={values.visitFrequency || ""}
                disabled={!canEdit}
                placeholder="e.g., 2x weekly"
                onChange={(e) => setValue("visitFrequency", e.target.value)}
              />
            </Field>
            <Field label="Next Scheduled Date">
              <DateFieldInput
                name="nextScheduledDate"
                value={values.nextScheduledDate}
                canEdit={canEdit}
                printMode={printMode}
                onChange={(v) => setValue("nextScheduledDate", v)}
              />
            </Field>
          </div>
        </Section>

        {/* 17. Additional Notes */}
        <Section icon={MedicalNotesIcon} title="17. Additional Notes">
          <textarea
            rows={3}
            className={`${textareaClass} min-h-[4.5rem]`}
            value={values.additionalNotes || ""}
            disabled={!canEdit}
            placeholder="Motivation, session observations, plan for next session…"
            onChange={(e) => setValue("additionalNotes", e.target.value)}
          />
        </Section>

        <FormSignatureSections
          {...signatureProps}
          canEdit={canEdit}
          canSign={canSign}
          printMode={printMode}
          values={values}
          onPatientSign={signatures.handlePatientSign}
          onClinicianSign={handleClinicianSign}
          onChange={setValues}
          clinicianLabel="Speech Therapist Signature"
        />

        {isReviewer ? (
          <OfficeReviewSection
            canEdit={canEdit}
            reviewComments={values.reviewComments}
            onChange={(v) => setValue("reviewComments", v)}
          />
        ) : values.reviewComments ? (
          <OfficeCommentsNote comments={values.reviewComments} />
        ) : null}

        {!printMode &&
          (permissions.canSendToOffice ||
            permissions.canToBeSign ||
            permissions.canReview ||
            permissions.canEdit) && (
            <FormActionBar
              permissions={permissions}
              status={form.status}
              canEdit={canEdit}
              saving={saving}
              onSubmit={submit}
            />
          )}
      </div>
    </div>
  );
};

StVisitNoteForm.propTypes = {
  patient: PropTypes.object,
  clinicalForm: PropTypes.object.isRequired,
  draft: PropTypes.object,
  onBack: PropTypes.func,
  onCreated: PropTypes.func,
  printMode: PropTypes.bool,
  onReady: PropTypes.func,
};

export default StVisitNoteForm;
